































import Popup from '@/components/default/Popup.vue';
import { QuotationLine } from '@/data/web-api-client';
import notificationService from '@/services/notificationService';
import { DxForm, DxGroupItem, DxItem, DxLabel, DxSimpleItem } from 'devextreme-vue/form';
import { dxNumberBoxOptions } from 'devextreme/ui/number_box';
import { dxTextBoxOptions } from 'devextreme/ui/text_box';
import { Component, Vue, Watch } from 'vue-property-decorator';

@Component({
  components: {
    Popup,
    DxForm,
    DxGroupItem,
    DxItem,
    DxLabel,
    DxSimpleItem,
  },
})
export default class DefineCostsDialog extends Vue {
  show: boolean = false;
  quotationLine: QuotationLine | null = null;

  saving: boolean = false;

  public open(line: QuotationLine) {
    this.quotationLine = JSON.parse(JSON.stringify(line));
    this.show = true;
  }

  closed() {
    this.show = false;
    this.quotationLine = null;
  }

  updateLabourCost(estimatedWorkMinutes: string) {
    if (!this.quotationLine || this.labourCostPerMinute == null || isNaN(this.labourCostPerMinute)) {
      return;
    }

    const calculatedLabourCost = Number(estimatedWorkMinutes) * this.labourCostPerMinute;
    if (isNaN(calculatedLabourCost)) {
      // something went wrong in calcs or input is wrong
      return;
    }

    // making sure JS doesn't do a weird rounding
    if (this.quotationLine.labourCost && Math.abs(this.quotationLine.labourCost - calculatedLabourCost) < 0.0001) {
      return;
    }

    // round till 0.0001
    this.quotationLine.labourCost = Math.round((calculatedLabourCost + Number.EPSILON) * 10000) / 10000;
  }

  updateEstimatedWorkMinutes(labourCost: string) {
    if (!this.quotationLine || this.labourCostPerMinute == null || isNaN(this.labourCostPerMinute)) {
      return;
    }

    const calculatedEstimatedWorkMinutes = Number(labourCost) / this.labourCostPerMinute;
    if (isNaN(calculatedEstimatedWorkMinutes)) {
      // something went wrong in calcs or input is wrong
      return;
    }

    // making sure JS doesn't do a weird rounding
    if (this.quotationLine.estimatedWorkMinutes && Math.abs(this.quotationLine.estimatedWorkMinutes - calculatedEstimatedWorkMinutes) < 0.0001) {
      return;
    }

    // round till 0.0001
    this.quotationLine.estimatedWorkMinutes = Math.round((calculatedEstimatedWorkMinutes + Number.EPSILON) * 10000) / 10000;
  }

  onFieldDataChanged(e: any) {
    if (!this.quotationLine) {
      return;
    }

    switch (e.dataField) {
      case 'estimatedWorkMinutes': {
        this.updateLabourCost(e.value);
        this.$nextTick(() => {
          this.form.instance?.repaint();
        });
        break;
      }
      case 'labourCost': {
        this.updateEstimatedWorkMinutes(e.value);
        this.$nextTick(() => {
          this.form.instance?.repaint();
        });
        break;
      }
    }
  }

  async save() {
    if (!this.quotationLine) {
      return;
    }

    try {
      const form: DxForm = this.form as DxForm;
      const validationResult = form.instance?.validate()!;

      if (validationResult.isValid) {
        this.saving = true;

        this.quotationLine.targetDurationOfArticle = this.quotationLine.estimatedWorkMinutes;
        this.quotationLine.originalLabourCost = this.quotationLine.labourCost;
        this.quotationLine!.cost = (this.quotationLine?.materialCost ?? 0) + (this.quotationLine?.labourCost ?? 0);
        this.$emit('updatedLine', this.quotationLine);
        notificationService.saved();
        this.closed();
      } else {
        notificationService.invalidForm();
      }
    } catch (error) {
      notificationService.failedToSave();
    } finally {
      this.saving = false;
    }
  }

  get disabledPropertyOptions(): dxTextBoxOptions {
    return {
      disabled: true,
    };
  }

  get propertyOptions(): dxNumberBoxOptions {
    return {
      step: 0,
    };
  }

  get estimatedWorkMinutesOptions(): dxNumberBoxOptions {
    const self = this;
    return {
      step: 0,
      onValueChanged(e) {
        self.updateLabourCost(e.value);
        self.$nextTick(() => {
          self.form.instance?.repaint();
        });
      },
    };
  }

  get labourCostOptions(): dxNumberBoxOptions {
    const self = this;
    return {
      step: 0,
      onValueChanged(e) {
        self.updateEstimatedWorkMinutes(e.value);
        self.$nextTick(() => {
          self.form.instance?.repaint();
        });
      },
    };
  }

  get labourCostPerMinute(): number | undefined {
    const settings = this.$store.getters['settings/data'];

    if (isNaN(settings.defaultHourRate)) {
      return;
    }

    return settings.defaultHourRate / 60;
  }

  get form(): DxForm {
    return this.$refs.defineCostsForm as DxForm;
  }
}
